import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import Loader from '../components/Loader';
import { getSystemReferralStats, getAllUsersReferralSummary } from '../services/referralService';
import { getReferralCommission } from '../services/settingsService';
import { formatUSD } from '../services/walletAnalyticsService';
import '../components/AdminLayout.css';

const PAGE_SIZE = 10;

// Postgres NUMERIC/BIGINT arrive from supabase-js as strings. Coerce before
// any arithmetic or comparison, or sorting silently goes lexicographic.
const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

// Sort accessors, keyed by column. Text columns default to ascending,
// numeric ones to descending.
const SORT_ACCESSORS = {
    name: (r) => (r.full_name || r.email || '').toLowerCase(),
    code: (r) => (r.referral_code || '').toLowerCase(),
    signups: (r) => num(r.referral_count),
    bonus: (r) => num(r.total_bonus_usd),
    status: (r) => (num(r.total_bonus_usd) > 0 ? 1 : 0),
    verified: (r) => num(r.verified_count),
    unverified: (r) => num(r.unverified_count),
};

const TEXT_COLUMNS = ['name', 'code'];

function AdminReferrals() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState({
        total_referred_users: 0,
        total_bonus_paid_usd: 0,
        active_referrers: 0,
        referrers_with_signups: 0,
        awaiting_payout_count: 0
    });
    const [commission, setCommission] = useState(null);
    const [referrers, setReferrers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [sort, setSort] = useState({ key: 'signups', dir: 'desc' });
    const [page, setPage] = useState(1);
    const [copiedCode, setCopiedCode] = useState(null);

    const fetchReferralData = useCallback(async () => {
        setError(null);
        try {
            const [statsRes, referrersRes, commissionValue] = await Promise.all([
                getSystemReferralStats(),
                getAllUsersReferralSummary(),
                getReferralCommission()
            ]);

            if (statsRes.success && statsRes.data) {
                setStats(statsRes.data);
            }
            if (referrersRes.success) {
                setReferrers(referrersRes.data || []);
            }
            setCommission(commissionValue);

            if (!statsRes.success || !referrersRes.success) {
                setError(statsRes.error || referrersRes.error || 'Could not load referral data.');
            }
        } catch (err) {
            console.error('Error fetching referral data:', err);
            setError(err.message || 'Could not load referral data.');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        const userRole = user?.user_metadata?.role || localStorage.getItem('role');
        if (String(userRole) !== '4') {
            navigate('/welcome');
            return;
        }
        fetchReferralData();
    }, [user, navigate, fetchReferralData]);

    // Rank is fixed by signups (then bonuses) so it doesn't shuffle when the
    // admin sorts by another column.
    const rankMap = useMemo(() => {
        const ordered = [...referrers].sort(
            (a, b) => num(b.referral_count) - num(a.referral_count) || num(b.total_bonus_usd) - num(a.total_bonus_usd)
        );
        const map = new Map();
        ordered.forEach((r, i) => map.set(r.user_id, i + 1));
        return map;
    }, [referrers]);

    const visible = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        const filtered = term
            ? referrers.filter(
                (r) =>
                    r.email?.toLowerCase().includes(term) ||
                    r.full_name?.toLowerCase().includes(term) ||
                    r.referral_code?.toLowerCase().includes(term)
            )
            : referrers;

        const accessor = SORT_ACCESSORS[sort.key] || SORT_ACCESSORS.signups;
        const factor = sort.dir === 'asc' ? 1 : -1;

        return [...filtered].sort((a, b) => {
            const av = accessor(a);
            const bv = accessor(b);
            if (av < bv) return -1 * factor;
            if (av > bv) return 1 * factor;
            // Stable tiebreak so equal rows never jitter between renders.
            return num(b.total_bonus_usd) - num(a.total_bonus_usd);
        });
    }, [referrers, searchTerm, sort]);

    useEffect(() => {
        setPage(1);
    }, [searchTerm, sort]);

    const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages);
    const pageRows = visible.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    const toggleSort = (key) => {
        setSort((prev) =>
            prev.key === key
                ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                : { key, dir: TEXT_COLUMNS.includes(key) ? 'asc' : 'desc' }
        );
    };

    const handleRefresh = () => {
        setRefreshing(true);
        fetchReferralData();
    };

    const handleCopyCode = (code) => {
        if (!code) return;
        navigator.clipboard
            ?.writeText(code)
            .then(() => {
                setCopiedCode(code);
                setTimeout(() => setCopiedCode(null), 2000);
            })
            .catch(() => { /* clipboard unavailable */ });
    };

    // Exports the filtered + sorted set, not just the visible page.
    const handleExportCsv = () => {
        const headers = [
            'Rank', 'Name', 'Email', 'Referral Code', 'Signups',
            'Bonuses Earned USD', 'Avg per Signup USD', 'Status',
            'Verified', 'Unverified', 'Last Signup', 'Last Bonus Paid'
        ];
        const cell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const rows = visible.map((r) => {
            const signups = num(r.referral_count);
            const bonus = num(r.total_bonus_usd);
            return [
                rankMap.get(r.user_id) ?? '',
                r.full_name || '',
                r.email || '',
                r.referral_code || '',
                signups,
                bonus.toFixed(2),
                signups > 0 ? (bonus / signups).toFixed(2) : '',
                bonus > 0 ? 'Credited' : 'Pending',
                num(r.verified_count),
                num(r.unverified_count),
                r.last_signup_at || '',
                r.last_referral_at || ''
            ].map(cell).join(',');
        });

        const csv = [headers.map(cell).join(','), ...rows].join('\r\n');
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `referral-leaderboard-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const StatCard = ({ title, value, sublabel, icon, colorClass, onClick }) => (
        <div
            className={`stat-card${onClick ? ' stat-card-link' : ''}`}
            onClick={onClick}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            onKeyDown={onClick ? (e) => (e.key === 'Enter' || e.key === ' ') && onClick() : undefined}
        >
            <div className={`stat-icon ${colorClass}`}>
                <i className={`bx ${icon}`}></i>
            </div>
            <div className="stat-content">
                <div className="stat-label">{title}</div>
                <div className="stat-value">{value}</div>
                {sublabel && <div className="stat-change">{sublabel}</div>}
            </div>
        </div>
    );

    const SortableTh = ({ label, sortKey, className = '', title }) => {
        const active = sort.key === sortKey;
        const icon = active ? (sort.dir === 'desc' ? 'bx-chevron-down' : 'bx-chevron-up') : 'bx-sort-alt-2';
        return (
            <th
                className={`sortable ${className}${active ? ' sorted' : ''}`}
                onClick={() => toggleSort(sortKey)}
                title={title || `Sort by ${label}`}
                aria-sort={active ? (sort.dir === 'desc' ? 'descending' : 'ascending') : 'none'}
            >
                {label}
                <i className={`bx sort-icon ${icon}`}></i>
            </th>
        );
    };

    if (loading) {
        return (
            <AdminLayout>
                <Loader />
            </AdminLayout>
        );
    }

    const signupTotal = num(stats.total_referred_users);
    const bonusTotal = num(stats.total_bonus_paid_usd);
    const withSignups = num(stats.referrers_with_signups) || referrers.length;
    const awaiting =
        stats.awaiting_payout_count !== undefined
            ? num(stats.awaiting_payout_count)
            : referrers.filter((r) => num(r.referral_count) > 0 && num(r.total_bonus_usd) === 0).length;

    return (
        <AdminLayout>
            <div className="page-header">
                <div>
                    <h1 className="page-title">
                        <i className="bx bx-gift"></i>
                        Referral Program
                    </h1>
                    <p className="page-subtitle">Track referral activity and bonuses across the platform</p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <button
                        className="btn btn-secondary"
                        onClick={handleExportCsv}
                        disabled={visible.length === 0}
                        title="Download the current view as CSV"
                    >
                        <i className="bx bx-download"></i>
                        Export CSV
                    </button>
                    <button className="btn btn-secondary" onClick={handleRefresh} disabled={refreshing}>
                        <i className={`bx bx-refresh ${refreshing ? 'bx-spin' : ''}`}></i>
                        {refreshing ? 'Refreshing…' : 'Refresh'}
                    </button>
                </div>
            </div>

            {error && (
                <div className="admin-alert admin-alert-danger">
                    <span className="admin-alert-message">
                        <i className="bx bx-error-circle"></i>
                        Could not load referral data: {error}
                    </span>
                    <button className="btn btn-sm btn-secondary" onClick={handleRefresh}>
                        Retry
                    </button>
                </div>
            )}

            {/* Stats */}
            <div className="stats-grid stats-grid-compact">
                <StatCard
                    title="Total Referral Signups"
                    value={signupTotal.toLocaleString('en-US')}
                    sublabel={`across ${withSignups} referrer${withSignups === 1 ? '' : 's'}`}
                    icon="bx-user-plus"
                    colorClass="primary"
                />
                <StatCard
                    title="Total Bonuses Earned"
                    value={formatUSD(bonusTotal)}
                    sublabel={signupTotal > 0 ? `${formatUSD(bonusTotal / signupTotal)} avg per signup` : null}
                    icon="bx-dollar-circle"
                    colorClass="success"
                />
                <StatCard
                    title="Referrers Paid"
                    value={num(stats.active_referrers).toLocaleString('en-US')}
                    sublabel={`of ${withSignups} with signups`}
                    icon="bx-share-alt"
                    colorClass="warning"
                />
                <StatCard
                    title="Awaiting Payout"
                    value={awaiting.toLocaleString('en-US')}
                    sublabel={awaiting === 0 ? 'all signups paid' : 'signups with no bonus'}
                    icon="bx-time-five"
                    colorClass={awaiting === 0 ? 'info' : 'danger'}
                />
                <StatCard
                    title="Bonus per Signup"
                    value={commission === null ? '—' : formatUSD(commission)}
                    sublabel="change in Settings"
                    icon="bx-cog"
                    colorClass="info"
                    onClick={() => navigate('/admin/settings')}
                />
            </div>

            {/* Leaderboard */}
            <div className="admin-card">
                <div className="card-header card-header-wrap">
                    <h3 className="card-title">
                        <i className="bx bx-list-ul"></i>
                        Referral Leaderboard ({visible.length})
                    </h3>
                    <div className="card-toolbar">
                        <div className="card-search">
                            <i className="bx bx-search"></i>
                            <input
                                type="text"
                                placeholder="Search name, email, or code…"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="form-input"
                            />
                        </div>
                    </div>
                </div>

                {visible.length === 0 ? (
                    <div className="admin-empty">
                        <i className={`bx ${searchTerm.trim() ? 'bx-search-alt' : 'bx-gift'}`}></i>
                        {searchTerm.trim() ? (
                            <>
                                <p>No referrers match “{searchTerm.trim()}”</p>
                                <button className="btn btn-sm btn-secondary" onClick={() => setSearchTerm('')}>
                                    Clear search
                                </button>
                            </>
                        ) : (
                            <p>{error ? 'Referral data unavailable.' : 'No referral activity yet'}</p>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="admin-table-wrap">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th className="num">#</th>
                                        <SortableTh label="Referrer" sortKey="name" />
                                        <SortableTh label="Code" sortKey="code" />
                                        <SortableTh label="Signups" sortKey="signups" className="num" />
                                        <SortableTh
                                            label="Verified"
                                            sortKey="verified"
                                            className="num"
                                            title="Referred users who confirmed their email address"
                                        />
                                        <SortableTh
                                            label="Unverified"
                                            sortKey="unverified"
                                            className="num"
                                            title="Referred users who have not confirmed their email yet — no bonus is due until they do"
                                        />
                                        <SortableTh label="Bonuses Earned" sortKey="bonus" className="num" />
                                        <SortableTh label="Status" sortKey="status" />
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageRows.map((r) => {
                                        const signups = num(r.referral_count);
                                        const bonus = num(r.total_bonus_usd);
                                        // Credited once any bonus has actually been paid; Pending until then.
                                        const credited = bonus > 0;
                                        const verified = num(r.verified_count);
                                        const unverified = num(r.unverified_count);
                                        return (
                                            <tr key={r.user_id}>
                                                <td className="num">{rankMap.get(r.user_id)}</td>
                                                <td>
                                                    <div className="user-cell">
                                                        <div className="user-avatar-small">
                                                            {(r.full_name || r.email || '?').charAt(0).toUpperCase()}
                                                        </div>
                                                        <div className="user-cell-text">
                                                            <button
                                                                className="link-button user-cell-name"
                                                                onClick={() => navigate(`/admin/users/${r.user_id}`)}
                                                                title={r.full_name || undefined}
                                                            >
                                                                {r.full_name || 'N/A'}
                                                            </button>
                                                            <div className="user-cell-sub" title={r.email}>
                                                                {r.email}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="code-cell">
                                                    <code className="code-chip">{r.referral_code || '—'}</code>
                                                    {r.referral_code && (
                                                        <button
                                                            className="link-button"
                                                            style={{ marginLeft: '0.5rem' }}
                                                            onClick={() => handleCopyCode(r.referral_code)}
                                                            title="Copy referral code"
                                                        >
                                                            <i
                                                                className={`bx ${
                                                                    copiedCode === r.referral_code ? 'bx-check' : 'bx-copy'
                                                                }`}
                                                            ></i>
                                                        </button>
                                                    )}
                                                </td>
                                                <td className="num">
                                                    <span
                                                        className={`badge ${signups > 0 ? 'badge-success' : 'badge-neutral'}`}
                                                    >
                                                        {signups}
                                                    </span>
                                                </td>
                                                <td className="num">
                                                    <span className={`badge ${verified > 0 ? 'badge-success' : 'badge-neutral'}`}>
                                                        {verified}
                                                    </span>
                                                </td>
                                                <td className="num">
                                                    <span className={`badge ${unverified > 0 ? 'badge-warning' : 'badge-neutral'}`}>
                                                        {unverified}
                                                    </span>
                                                </td>
                                                <td
                                                    className="num"
                                                    title={
                                                        signups > 0
                                                            ? `${formatUSD(bonus / signups)} average per signup`
                                                            : undefined
                                                    }
                                                >
                                                    {formatUSD(bonus)}
                                                </td>
                                                <td>
                                                    {credited ? (
                                                        <span
                                                            className="badge badge-success"
                                                            title={`${formatUSD(bonus)} credited to this referrer's wallet`}
                                                        >
                                                            Credited
                                                        </span>
                                                    ) : signups > 0 ? (
                                                        <span
                                                            className="badge badge-warning"
                                                            title="No bonus paid yet — the referred user has not verified their email"
                                                        >
                                                            Pending
                                                        </span>
                                                    ) : (
                                                        <span className="badge badge-neutral">—</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <button
                                                        className="btn btn-sm btn-secondary"
                                                        onClick={() => navigate(`/admin/referrals/${r.user_id}`)}
                                                        title="Open the full referral breakdown for this user"
                                                    >
                                                        <i className="bx bx-show"></i>
                                                        View
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {totalPages > 1 && (
                            <div className="admin-pagination">
                                <span className="pagination-info">
                                    Showing {(currentPage - 1) * PAGE_SIZE + 1}–
                                    {Math.min(currentPage * PAGE_SIZE, visible.length)} of {visible.length} referrers
                                </span>
                                <div className="pagination-controls">
                                    <button
                                        className="btn btn-sm btn-secondary"
                                        onClick={() => setPage(currentPage - 1)}
                                        disabled={currentPage <= 1}
                                    >
                                        <i className="bx bx-chevron-left"></i>
                                        Prev
                                    </button>
                                    <span className="pagination-page">
                                        Page {currentPage} of {totalPages}
                                    </span>
                                    <button
                                        className="btn btn-sm btn-secondary"
                                        onClick={() => setPage(currentPage + 1)}
                                        disabled={currentPage >= totalPages}
                                    >
                                        Next
                                        <i className="bx bx-chevron-right"></i>
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </AdminLayout>
    );
}

export default AdminReferrals;
